import { useLazyQuery, useQuery } from "@apollo/client";
import { Container, IconButton, Paper, Typography } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridEventListener,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import { FILES, GET_HELIOS_FILE } from "./gql";
import { useEffect, useState } from "react";
import { csCZ } from "@mui/x-data-grid/locales";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { DateTime } from "luxon";
import LoadingCircle from "../../../components/LoadingCircle";
import { useNavigate } from "react-router-dom";
import { FileTableType } from "../../../utils/Types/FileTableType";
import { openLink, translateEntityKind } from "../../../utils/FileTableUtils";
import ErrorLayout from "../../../components/layout/ErrorLayout";
import { enqueueSnackbar } from "notistack";

interface FileTableRowsDataType extends FileTableType {
  action: any;
}

type FileTableActionType = "download" | "openLink" | "downloadHelios";

const FileTable = () => {
  const navigate = useNavigate();

  const { loading, error, data } = useQuery(FILES);
  
  const [getHeliosFile, { loading: heliosFileLoading }] =
    useLazyQuery(GET_HELIOS_FILE);

  const [rowsData, setRowsData] = useState<FileTableRowsDataType[]>([]);
  
  const [rowSelectionModel, setRowSelectionModel] =
  useState<GridRowSelectionModel>([]);

  const downloadFile = (id: string) => {
    const href = `${process.env.REACT_APP_SERVER_URL}/api/file/${id}`;
    window.open(href);
  };

const handleAction = (
    e: React.MouseEvent,
    action: FileTableActionType,
    actionValue: string
  ) => {
    if (action === "download") {
      downloadFile(actionValue);
    } else if (action === "downloadHelios") {
      if (rowSelectionModel.length > 0) {
        const selectedHeliosRows = rowSelectionModel.map((id) =>
          rowsData.find((row) => row.id === id)
        );

        const parsedHeliosRows = selectedHeliosRows.filter(
          (item) => item !== undefined && item.externalLink !== null
        );

        if (parsedHeliosRows.length > 0) {
          downloadHeliosFile(
            parsedHeliosRows.map((row) => row?.externalLink).join(",")
          );
        }

        const rowsNotInHelios = rowSelectionModel.map((id) =>
          rowsData.find((row) => row.externalLink === null && row.id === id)
        );

        const parsedRowsNotInHelios = rowsNotInHelios.filter(
          (item) => item !== undefined
        );

        if (parsedRowsNotInHelios.length > 0) {
          downloadZipFile(
            parsedRowsNotInHelios.map((row) => row?.id).join(",")
          ).catch((error) => {
            console.error(error);
            enqueueSnackbar("Soubory se nepodařilo stáhnout", {
              variant: "error",
            });
          });
        }
      } else {
        downloadHeliosFile(actionValue);
      }
    } else {
      openLink(actionValue);
    }
    e.stopPropagation();
  };
  
  const downloadZipFile = async (ids: string) => {
    const href = `${process.env.REACT_APP_SERVER_URL}/api/file/zip/${ids}`;
    window.open(href);
  };

  const downloadHeliosFile = (guid: string) => {
    getHeliosFile({
      variables: { id: guid },
      onCompleted(data) {
        const link = document.createElement("a");
        link.href =
          "data:application/octet-stream;base64," + data.heliosFile.fileData;
        link.download = data.heliosFile.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },
      onError(error) {
        console.log(error);
        enqueueSnackbar("Soubor se nepodařilo stáhnout", { variant: "error" });
      },
    });
  };

  const handleRowClick: GridEventListener<"rowClick"> = (
    params, // GridRowParams
    event, // MuiEvent<React.MouseEvent<HTMLElement>>
    details // GridCallbackDetails
  ) => {
    navigate(`/dokumenty/${params.row.id}`);
    console.log(params);
  };

  useEffect(() => {
    if (data) {
      let flattenRowData: FileTableRowsDataType[] = [];

      data.fileTableAll.forEach((rowItem: any) => {
        flattenRowData.push({
          id: rowItem.id,
          createdAt: rowItem.createdAt,
          createdBy: rowItem.createdBy.username,
          owner: rowItem.owner.username,
          filename: rowItem.filename,
          entityKind: rowItem.entityKind,
          action: rowItem,
          anotherInfo: rowItem.anotherInfo,
          externalLink: rowItem.externalLink,
        });
      });

      setRowsData(flattenRowData);
    }
  }, [data]);

  const columns: GridColDef[] = [
    {
      headerName: "Vytvořeno",
      field: "createdAt",
      width: 200,
      valueFormatter: (value) =>
        DateTime.fromISO(value).toFormat("d.M.yyyy HH:mm"),
    },
    { headerName: "Nahrál", field: "createdBy", width: 150 },
    { headerName: "Vlastník", field: "owner" },
    {
      headerName: "Název souboru",
      width: 200,
      field: "filename",
    },
    {
      headerName: "Druh souboru",
      width: 80,
      field: "entityKind",
      flex: 1,
      valueGetter: (value, row) =>
        translateEntityKind(data.enumerations, row.entityKind),
    },
    {
      headerName: "Akce",
      field: "action",
      renderCell: (props: GridRenderCellParams<any, FileTableRowsDataType>) => {
        if (props.value) {
          if (
            props.value.externalLink &&
            props.value.externalLink.includes("http")
          ) {
            return (
              <IconButton
                onClick={(e: React.MouseEvent) =>
                  handleAction(
                    e,
                    "openLink",
                    props.value ? props.value.externalLink : ""
                  )
                }
              >
                <OpenInNewIcon />
              </IconButton>
            );
          } else if (props.value.externalLink) {
            return (
              <IconButton
                disabled={heliosFileLoading}
                onClick={(e: React.MouseEvent) =>
                  handleAction(
                    e,
                    "downloadHelios",
                    props.value ? props.value.externalLink : ""
                  )
                }
              >
                <CloudDownloadIcon />
              </IconButton>
            );
          } else {
            return (
              <IconButton
                onClick={(e: React.MouseEvent) =>
                  handleAction(e, "download", props.value ? props.value.id : "")
                }
              >
                <DownloadIcon />
              </IconButton>
            );
          }
        }

        return "";
      },
    },
  ];

  const paginationModel = { page: 0, pageSize: 10 };

  if (loading) return <LoadingCircle />;
  if (error) return <ErrorLayout errorMsg={error.message || ""} />;

  return (
    <Container>
      <Typography variant="h2" mb={2}>
        Šanon
      </Typography>
      <Paper sx={{ width: "100%" }}>
        <DataGrid
          rows={rowsData}
          columns={columns}
          initialState={{
            pagination: { paginationModel },
            sorting: { sortModel: [{ field: "createdAt", sort: "desc" }] },
          }}
          pageSizeOptions={[10, 15]}
          checkboxSelection
          sx={{
            border: 0,
            "& .MuiDataGrid-row:hover": {
              cursor: "pointer",
            },
            "& .MuiCheckbox-root.Mui-checked": { color: "#CC0000" },
            "& .MuiCheckbox-root.MuiCheckbox-indeterminate": { color: "#CC0000" },
          }}
          disableRowSelectionOnClick
          onRowClick={handleRowClick}
          localeText={csCZ.components.MuiDataGrid.defaultProps.localeText}
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
        />
      </Paper>
    </Container>
  );
};

export default FileTable;
